import { useEffect, useRef, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts'
import type { Mix, Recipe, Ingredient, Ownership, InvitationDTO, InvitationStatus } from '@/types'

// ─── Conversion helpers ─────────────────────────────────────────────────────

/**
 * Convert a Supabase postgres_changes row to the frontend Mix type.
 *
 * Supabase returns snake_case DB columns. The frontend expects camelCase.
 * This helper also computes the Ownership object client-side.
 */
export function convertRealtimeRowToMix(
  row: Record<string, unknown> | undefined,
  currentUserId: string | undefined
): Mix | null {
  if (!row) return null

  const ownership: Ownership = {
    isOwner: row.owner_id === currentUserId,
    canEdit: row.owner_id === currentUserId || (row.contributors as string[])?.includes(currentUserId || ''),
    canView: true,
  }

  const recipes = (row.recipes as Recipe[]) || []
  const ingredients = (row.ingredients as Ingredient[]) || []
  const contributors = (row.contributors as string[]) || []

  const mix: Mix = {
    id: row.id as string,
    name: row.name as string,
    type: (row.type as Mix['type']) || 'standard',
    startedAt: row.started_at ? (row.started_at as string) : undefined,
    numberOfDays: row.number_of_days ? (row.number_of_days as number) : undefined,
    mealSlotOrder: row.meal_slot_order ? (row.meal_slot_order as Mix['mealSlotOrder']) : undefined,
    recipes,
    ingredients,
    ownerId: row.owner_id as string,
    ownerName: (row.owner_name as string) || 'Unknown',
    ownerImage: row.owner_image as string | null | undefined,
    contributors,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    archivedAt: row.archived_at ? (row.archived_at as string) : undefined,
    ownership,
  }

  return mix
}

export function convertRealtimeRowToInvitation(
  row: Record<string, unknown> | undefined,
  currentUserId: string | undefined
): InvitationDTO | null {
  if (!row) return null

  const usedById = row.used_by_id as string | undefined
  const isUsedByMe = usedById === currentUserId

  const dto: InvitationDTO = {
    id: row.id as string,
    mixId: row.mix_id as string,
    mixName: row.mix_name as string,
    sharerName: row.sharer_name as string,
    sharerImage: row.sharer_image as string,
    status: row.status as InvitationStatus,
    expiresAt: row.expires_at as string,
    usedAt: row.used_at ? (row.used_at as string) : null,
    usedByName: row.used_by_name as string | undefined,
    usedByImage: row.used_by_image as string | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    isUsedByMe,
    ownership: null,
  }

  return dto
}

// ─── Callback types ─────────────────────────────────────────────────────────

export interface RealtimeStateCallbacks {
  onMixInsert?: (mix: Mix) => void
  onMixUpdate?: (mix: Mix) => void
  onMixDelete?: (id: string) => void
  onInvitationInsert?: (invitation: InvitationDTO) => void
  onInvitationUpdate?: (invitation: InvitationDTO) => void
  onInvitationDelete?: (id: string) => void
  onRecipeProgress?: (payload: Record<string, unknown>) => void
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Phase 1.4: Realtime subscription layer that drives React state.
 *
 * Subscribes to postgres_changes on mixes, invitations, and recipe_processing_updates.
 * Converts DB rows to frontend types and invokes supplied callbacks.
 *
 * Wired into WebSocketProvider so it runs alongside (and eventually replaces)
 * the existing WebSocket.
 */
export function useSupabaseRealtime(callbacks: RealtimeStateCallbacks = {}) {
  const channelsRef = useRef<ReturnType<typeof supabase.channel>[]>([])
  const { user } = useAuth()
  const currentUserId = user?.id
  const callbacksRef = useRef(callbacks)

  // Keep callback ref current without re-subscribing
  useEffect(() => { callbacksRef.current = callbacks }, [callbacks])

  const logConvertedMix = useCallback((eventType: string, row: Record<string, unknown> | undefined) => {
    const mix = convertRealtimeRowToMix(row, currentUserId)
    if (mix) {
      console.debug(`[RealtimeConvert] ${eventType}:`, mix)
    }
    return mix
  }, [currentUserId])

  const processInvitation = useCallback((row: Record<string, unknown> | undefined, eventType: string) => {
    const invitation = convertRealtimeRowToInvitation(row, currentUserId)
    if (invitation) {
      console.debug(`[RealtimeConvert] invitation ${eventType}:`, invitation)
    }
    return invitation
  }, [currentUserId])

  useEffect(() => {
    const recipeChannel = supabase
      .channel('table:recipe_processing_updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'recipe_processing_updates' },
        (payload) => {
          console.debug('[Realtime] recipe processing INSERT raw:', payload)
          callbacksRef.current.onRecipeProgress?.(payload.new as Record<string, unknown>)
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'recipe_processing_updates' },
        (payload) => {
          console.debug('[Realtime] recipe processing UPDATE raw:', payload)
          callbacksRef.current.onRecipeProgress?.(payload.new as Record<string, unknown>)
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'recipe_processing_updates' },
        (payload) => {
          console.debug('[Realtime] recipe processing DELETE raw:', payload)
        }
      )
      .subscribe((status) => {
        console.debug('[Realtime] recipe processing channel status:', status)
      })

    const mixChannel = supabase
      .channel('table:mixes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'mixes' },
        (payload) => {
          console.debug('[Realtime] mix INSERT raw:', payload)
          const mix = logConvertedMix('INSERT', payload.new as Record<string, unknown>)
          if (mix) callbacksRef.current.onMixInsert?.(mix)
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'mixes' },
        (payload) => {
          console.debug('[Realtime] mix UPDATE raw:', payload)
          const mix = logConvertedMix('UPDATE', payload.new as Record<string, unknown>)
          if (mix) callbacksRef.current.onMixUpdate?.(mix)
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'mixes' },
        (payload) => {
          console.debug('[Realtime] mix DELETE raw:', payload)
          logConvertedMix('DELETE', payload.old as Record<string, unknown>)
          const id = (payload.old as Record<string, unknown>)?.id as string
          if (id) callbacksRef.current.onMixDelete?.(id)
        }
      )
      .subscribe((status) => {
        console.debug('[Realtime] mixes channel status:', status)
      })

    const invitationChannel = supabase
      .channel('table:invitations')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'invitations' },
        (payload) => {
          console.debug('[Realtime] invitation INSERT raw:', payload)
          const invitation = processInvitation(payload.new as Record<string, unknown>, 'INSERT')
          if (invitation) callbacksRef.current.onInvitationInsert?.(invitation)
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'invitations' },
        (payload) => {
          console.debug('[Realtime] invitation UPDATE raw:', payload)
          const invitation = processInvitation(payload.new as Record<string, unknown>, 'UPDATE')
          if (invitation) callbacksRef.current.onInvitationUpdate?.(invitation)
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'invitations' },
        (payload) => {
          console.debug('[Realtime] invitation DELETE raw:', payload)
          const id = (payload.old as Record<string, unknown>)?.id as string
          if (id) callbacksRef.current.onInvitationDelete?.(id)
        }
      )
      .subscribe((status) => {
        console.debug('[Realtime] invitations channel status:', status)
      })

    channelsRef.current = [recipeChannel, mixChannel, invitationChannel]

    return () => {
      channelsRef.current.forEach((ch) => supabase.removeChannel(ch))
    }
  }, [logConvertedMix, processInvitation])
}
